import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Megaphone, Send } from "lucide-react";
import { adminApi, formatApiError } from "../../lib/adminApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const TEMPLATES = [
  {
    id: "launch-day",
    label: "Launch Day",
    subject: "We're live — Community Hearts is officially open",
    message:
      "The wait is over. Community Hearts is live, and your spot is ready.\n\nNo games, no endless swiping — just verified people who actually want to meet. Matches expire in 120 hours, so every conversation starts with intent.\n\nOpen the app, complete your verification, and say hello to someone real.\n\n— The Community Hearts team",
  },
  {
    id: "city-unlocked",
    label: "City Unlocked",
    subject: "Your city just unlocked on Community Hearts",
    message:
      "Big news — thanks to you and your friends, your city has hit its launch goal.\n\nDoors open soon for your area. Keep an eye on your inbox, and make sure your profile is ready: verified, recent photos, and a bio that actually sounds like you.\n\n— The Community Hearts team",
  },
  {
    id: "hearts-plus",
    label: "Hearts+ Announcement",
    subject: "Introducing Hearts+ — dating, upgraded",
    message:
      "Hearts+ is here: priority placement, extended match windows, and first access to members-only events.\n\nAs a founding waitlist member, you'll get an exclusive offer before anyone else. Watch this space.\n\n— The Community Hearts team",
  },
  {
    id: "event-invite",
    label: "Event Invite",
    subject: "You're invited — a Community Hearts evening near you",
    message:
      "We're hosting a members-only mixer, and you're on the list.\n\nReal people, verified profiles, zero pressure — just a room full of singles who are done playing games. RSVP in the app before spots fill.\n\n— The Community Hearts team",
  },
];

export const NotifyTab = () => {
  const [cities, setCities] = useState([]);
  const [segment, setSegment] = useState("all");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle");
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/milestones");
    setCities(data.map((c) => ({ city: c.city, count: c.count })));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const audience = useMemo(() => {
    if (segment === "all") return cities.reduce((s, c) => s + c.count, 0);
    if (segment === "new7d") return null;
    if (segment.startsWith("city:")) return cities.find((c) => c.city === segment.slice(5))?.count ?? 0;
    return null;
  }, [segment, cities]);

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setMessage(t.message);
    setState("idle");
    setResult(null);
  };

  const send = async (e) => {
    e.preventDefault();
    setState("sending");
    setResult(null);
    try {
      const { data } = await adminApi.post("/admin/broadcast", { subject, message, segment });
      setResult(data);
      setState("sent");
      setSubject("");
      setMessage("");
    } catch (err) {
      setState("error:" + formatApiError(err?.response?.data?.detail || "Send failed"));
    }
  };

  return (
    <div className="max-w-2xl" data-testid="notify-tab">
      <div className="rounded-3xl border border-cream/10 p-6">
        <p className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">Notification sender</p>
        <p className="text-sm leading-relaxed text-cream/50">
          Sends a branded email today — push notifications activate when the app launches. Members who opted out or are suspended/banned are skipped automatically.
        </p>
        <div className="mt-6">
          <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">
            <Megaphone className="h-3.5 w-3.5 text-gold" />
            Quick templates
          </p>
          <div className="flex flex-wrap gap-2" data-testid="notify-templates">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => applyTemplate(t)}
                data-testid={`notify-template-${t.id}`}
                className="rounded-full border border-cream/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-cream/60 transition-colors duration-200 hover:border-gold hover:text-gold active:scale-95"
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <form onSubmit={send} className="mt-6 space-y-4">
          <div className="relative">
            <select
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              aria-label="Audience segment"
              data-testid="notify-segment"
              className="h-12 w-full cursor-pointer appearance-none rounded-full border border-cream/20 bg-cream/5 px-5 pr-10 text-sm font-semibold text-cream outline-none"
            >
              <option value="all" className="text-ink">All members</option>
              <option value="new7d" className="text-ink">New signups — last 7 days</option>
              {cities.map((c) => (
                <option key={c.city} value={`city:${c.city}`} className="text-ink">
                  {c.city} ({c.count})
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-cream/50" />
          </div>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            maxLength={200}
            placeholder="Subject line"
            aria-label="Subject"
            data-testid="notify-subject"
            className="h-12 rounded-full border-cream/20 bg-cream/5 px-5 text-cream placeholder:text-cream/40 focus-visible:ring-2 focus-visible:ring-gold"
          />
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            maxLength={5000}
            rows={5}
            placeholder="Your message…"
            aria-label="Message"
            data-testid="notify-message"
            className="w-full rounded-3xl border border-cream/20 bg-cream/5 px-5 py-4 text-sm leading-relaxed text-cream placeholder:text-cream/40 outline-none focus-visible:ring-2 focus-visible:ring-gold"
          />
          <div className="flex flex-wrap items-center gap-4">
            <Button
              type="submit"
              disabled={state === "sending"}
              data-testid="notify-send"
              className="h-12 rounded-full bg-gold px-8 text-xs font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-200 hover:bg-gold-deep active:scale-95"
            >
              <Send className="mr-2 h-4 w-4" />
              {state === "sending" ? "Sending…" : "Send broadcast"}
            </Button>
            {audience !== null && (
              <span className="text-xs uppercase tracking-[0.15em] text-cream/40" data-testid="notify-audience">
                ~{audience} recipient{audience === 1 ? "" : "s"}
              </span>
            )}
          </div>
          {state === "sent" && result && (
            <p className="text-sm font-semibold text-gold" data-testid="notify-result">
              Sent to {result.sent} of {result.audience} members — {result.segment}.
            </p>
          )}
          {state.startsWith("error:") && (
            <p className="text-sm font-semibold text-red-400" data-testid="notify-error">{state.slice(6)}</p>
          )}
        </form>
      </div>
    </div>
  );
};
